import type { Response, Task, TaskQueryParams, TaskStatus, TaskUser, TaskUserRequest } from '~/models/AssignTask'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import useAuthStore from '~/stores/auth'
import { externalClient } from '~/api/client/externalClient'

export const useTasksApi = () => {
  const { userId } = storeToRefs(useAuthStore())

  const buildQuery = (params: TaskQueryParams): string => {
    const queryString = Object.entries(params)
      .filter(([_, value]) => {
        // Handle both arrays and numbers
        if (Array.isArray(value)) {
          return value && value.length > 0
        }
        // For numbers, check if it's not null/undefined
        return value !== null && value !== undefined
      })
      .map(([key, value]) => {
        // Handle arrays
        if (Array.isArray(value)) {
          return `${key}=${encodeURIComponent((value as string[]).join(','))}`
        }
        // Handle numbers and other primitives
        return `${key}=${encodeURIComponent(String(value))}`
      })
      .join('&')

    return queryString ? `?${queryString}` : ''
  }

  function mapTaskResponse(data: any): Task {
    return {
      ...data,
      startDate: DateTime.fromISO(data.startDate, { zone: 'UTC' })
    }
  }

  return {
    getTasks: async (filters: TaskQueryParams = {}, limit: number = 50, page: number = 1): Promise<Response> => {
      const query = buildQuery({ ...filters })
      const { data } = await externalClient.get<Response>(`/frontend/tasks${query}&pageSize=${limit}&pageNumber=${page}`)
      return {
        response: data.response.map(mapTaskResponse),
        resultCount: data.resultCount,
      }
    },

    createTask: async (task: Partial<Task>): Promise<Task> =>
      mapTaskResponse((await externalClient.post('/frontend/tasks', task)).data),

    updateTask: async (task: Task): Promise<Task> =>
      mapTaskResponse((await externalClient.put(`/frontend/tasks/${task.taskId}`, task)).data),

    deleteTask: async (task: Task): Promise<void> =>
      (await externalClient.delete(`/frontend/tasks/${task.taskId}`)).data,

    closeTask: async (task: Task): Promise<void> =>
      await externalClient.patch(`/frontend/tasks/${task.taskId}/close`),

    openTask: async (task: Task): Promise<void> =>
      await externalClient.patch(`/frontend/tasks/${task.taskId}/open`),

    getTaskUsers: async (task: Task): Promise<TaskUser[]> =>
      (await externalClient.get<TaskUser[]>(`/frontend/tasks/${task.taskId}/users`)).data,

    addTaskUsers: async (task: Task, users: TaskUserRequest): Promise<Task> =>
      mapTaskResponse((await externalClient.put<Task>(`/frontend/tasks/${task.taskId}/users`, users)).data),

    updateTaskUser: async (task: Task, user: TaskUser, status: TaskStatus): Promise<void> =>
      await externalClient.patch(`/frontend/tasks/${task.taskId}/users/${user.userId}?status=${status}`),
  }
}
